// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ButtonIcon } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import { IconWrapper } from "@insite/mobius/Icon";
import ArrowDown from "@insite/mobius/Icons/ArrowDown";
import ArrowUp from "@insite/mobius/Icons/ArrowUp";
import { getProp } from "@insite/mobius/utilities";
import { ArrowPosition, ArrowType, NavigationProps, useArrowElement } from "@sc/App/Components/XngCarousel";
import ChevronLeft from "@sc/XDL/Icons/ChevronLeft";
import ChevronRight from "@sc/XDL/Icons/ChevronRight";
import { color, xClassNames, xScreen } from "@sc/XDL/Utilities";
import React, { FC, useCallback } from "react";
import { css } from "styled-components";
import { useSwiper } from "swiper/react";

const generateArrowStyle = (arrowPosition: ArrowPosition) => ({
    size: 16,
    css: css`
        position: static;
        ${`margin-${arrowPosition === "left" ? "right" : "left"}: 20px`};
        background: transparent;
        & > ${IconWrapper} {
            color: ${color("common.ui4")};
            transition: all ${getProp("theme.transition.duration.regular", "300")}ms ease-in-out;
            &:hover {
                color: ${color("common.ui8")};
            }
        }
        transform: none;
        z-index: 10;
        padding: 0;
        border-radius: 50%;
        display: flex;
        justify-content: center;
        align-items: center;
        border: 0;
        outline: none !important;

        &:focus {
            box-shadow: none;
        }

        ${(arrowPosition === "top" || arrowPosition === "bottom") &&
        css`
            position: relative;
            inset: 0;
            transform: none;
            margin: auto;
        `};

        &.swiper-button-disabled {
            & > ${IconWrapper} {
                color: ${color("common.ui4", 0.3)};
            }
        }

        ${xScreen("lte", "md")} {
            display: none;
        }
    `,
});
const generateBackGroundStyle = ({
    arrowPosition,
    disabled = false,
}: {
    arrowPosition: ArrowPosition;
    disabled: boolean | undefined;
}) => ({
    css: css`
        position: absolute;
        height: 100%;
        top: 0;
        width: 64px;
        z-index: 1;
        background: linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, #ffffff 100%);

        ${arrowPosition === "left" &&
        css`
            left: 0;
            rotate: 180deg;
        `};

        ${arrowPosition === "right" &&
        css`
            right: 0;
        `};

        ${disabled &&
        css`
            display: none;
        `};

        ${xScreen("lte", "md")} {
            display: none;
        }
    `,
});
const arrowNavigationIcons = {
    top: {
        src: ArrowUp,
        size: 18,
    },
    bottom: {
        src: ArrowDown,
        size: 18,
    },
    left: {
        src: ChevronLeft,
        size: 18,
    },
    right: {
        src: ChevronRight,
        size: 18,
    },
};

const getArrowPosition = (arrowType: ArrowType, direction: "horizontal" | "vertical" = "horizontal"): ArrowPosition => {
    const isVertical = direction === "vertical";
    if (arrowType === "next") {
        return isVertical ? "bottom" : "right";
    }
    // when arrowType='prev'.
    return isVertical ? "top" : "left";
};

const ArrowNavigation: FC<NavigationProps> = React.memo(
    ({ arrowType, showOnMobile, buttonIconProps, ...otherProps }) => {
        const swiperInstance = useSwiper();
        const arrowPosition = getArrowPosition(arrowType, swiperInstance?.isHorizontal() ? "horizontal" : "vertical");
        const { src: iconSrc, size: iconSize } = arrowNavigationIcons[arrowPosition];

        const {
            className,
            element: arrowElement,
            disabled,
        } = useArrowElement({
            swiper: swiperInstance,
            showOnMobile,
            arrowType,
        });

        const onClickHandler = useCallback(
            (event: any) => {
                arrowElement?.click();
                otherProps.onClick?.(event);
            },
            [arrowElement],
        );

        if (!swiperInstance || !arrowElement) {
            return null;
        }

        const props = {
            arrowPosition,
            showOnMobile,
            className: xClassNames.append(className, otherProps.className),
        };

        return (
            <>
                <StyledWrapper {...generateBackGroundStyle({ arrowPosition, disabled })}></StyledWrapper>
                <Clickable
                    {...props}
                    {...generateArrowStyle(arrowPosition)}
                    aria-label={`carousel-${arrowPosition}`}
                    onClick={onClickHandler}
                >
                    <ButtonIcon
                        src={iconSrc}
                        size={iconSize}
                        color="common.white"
                        className={`carousel-${arrowPosition}-icon ${disabled && "disabled"}`}
                        {...buttonIconProps}
                    />
                </Clickable>
            </>
        );
    },
);

export default ArrowNavigation;
